/**
 * Share modal: title, description, read-only share text, Twitter / LinkedIn / Copy link / Close.
 * Used for both "Share this checklist" (main page) and "Share Your Results" (results page).
 */
import React from 'react';

import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { SOCIAL_SHARE, SITE_URL } from '../constants';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareText: string;
  title: string;
  description: React.ReactNode;
  onCopyLink: () => void;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onOpenChange,
  shareText,
  title,
  description,
  onCopyLink,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={4}
          value={shareText}
          readOnly
          className="mb-4"
          aria-label="Share text"
        />
        <div className="flex flex-col gap-3">
          <Button asChild variant="default" size="default" className="w-full">
            <a
              href={`${SOCIAL_SHARE.twitter.baseUrl}?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(SITE_URL)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on Twitter"
            >
              Share on Twitter
            </a>
          </Button>
          <Button asChild variant="default" size="default" className="w-full bg-blue-700 hover:bg-blue-800">
            <a
              href={`${SOCIAL_SHARE.linkedin.baseUrl}?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
            >
              Share on LinkedIn
            </a>
          </Button>
          <Button variant="outline" size="default" className="w-full" onClick={onCopyLink} aria-label="Copy link">
            Copy Link
          </Button>
          <Button variant="secondary" size="default" className="w-full" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
